import React, { useState } from 'react';
import { Modal, Button, Group, Stack } from '@good/ui/core';
import { notification } from 'antd';
import { isEmpty } from 'lodash';
import { IRootDispatch } from '../../../../../stores/rematch/root-store';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { PermanentlyRemoveUserModalStep1 } from './PermanentlyRemoveUserModalStep1';
import { PermanentlyRemoveUserModalStep2 } from './PermanentlyRemoveUserModalStep2';
import { PermanentlyRemoveUserModalStep2ImpactedShifts } from './PermanentlyRemoveUserModalStep2ImpactedShifts';
import { TeamMember } from 'interfaces/team-member-interface';
import { useFetchCompanyDataLite } from 'utilities/react-hooks/use-fetch-company-data-lite';

type IPermanentlyRemoveUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  worker: TeamMember;
};

interface FutureShift {
  supportWorkerAttendanceId: string;
  serviceDateTimeId: string;
  startDateTime: string;
  endDateTime: string;
  status: string;
  serviceName: string;
  serviceType: string;
  formattedDate: string;
  formattedTime: string;
}

const PermanentlyRemoveUserModal: React.FC<IPermanentlyRemoveUserModalProps> = ({ onClose, worker, isOpen }) => {
  const { teamStore } = useDispatch<IRootDispatch>();
  const { doRemoveTeamMember, doViewTeamMemberFutureShifts } = teamStore;
  const companyDataLite = useFetchCompanyDataLite();
  const timezone = companyDataLite?.timezone || moment.tz.guess();

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [isSelectFutureDate, setIsSelectFutureDate] = useState(false);
  const [removalDate, setRemovalDate] = useState<Date | null>(null);
  const [title, setTitle] = useState('Remove');
  const [impactedShifts, setImpactedShifts] = useState<FutureShift[]>([]);

  const onModalClose = () => {
    setTitle('Remove');
    setStep(1);
    setIsLoading(false);
    setRemoveReason('');
    setRemovalDate(null);
    setIsSelectFutureDate(false);
    setImpactedShifts([]);
    onClose();
  };

  const onModalSave = async () => {
    if (!removeReason || !removeReason.trim() || removeReason.length < 5) {
      return;
    }
    if (isSelectFutureDate && !removalDate) {
      return;
    }

    setIsLoading(true);

    try {
      if (isSelectFutureDate) {
        // Stage 1: Just fetch impacted shifts and move to stage 2
        // Don't schedule removal yet - only do that when user confirms in stage 2
        // Set to end of day in provider's timezone so workers can be assigned to bookings throughout the entire removal date
        const removalDateISO = moment.tz(removalDate, timezone).endOf('day').toISOString();

        // Fetch impacted shifts and move to stage 2
        try {
          const shiftsResponse = await doViewTeamMemberFutureShifts({
            supportWorkerId: worker.supportWorkerId,
            removalDate: removalDateISO,
          });

          setImpactedShifts(shiftsResponse.shifts || []);
          setTitle('Impacted future shifts for');
          setStep(2); // Move to stage 2 (impacted shifts)
        } catch (shiftError) {
          console.error('Error fetching future shifts:', shiftError);
          // If shift fetching fails, go to success step
          setTitle('User successfully scheduled for removal');
          setStep(3);
        }
      } else {
        // Immediate removal - this can happen immediately
        const payload = { removeReason: removeReason, supportWorkerId: worker.supportWorkerId };
        await doRemoveTeamMember(payload);
        setTitle('User successfully removed');
        setStep(3);
      }
    } catch (e) {
      notification.error({ message: 'Something went wrong, please try again.' });
    }

    setIsLoading(false);
  };

  const onCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSelectFutureDate(event.target.checked);
  };

  const onConfirmRemoval = async () => {
    if (!removalDate) {
      return;
    }

    try {
      setIsLoading(true);

      // Set to end of day in provider's timezone so workers can be assigned to bookings throughout the entire removal date
      const removalDateISO = moment.tz(removalDate, timezone).endOf('day').toISOString();

      // Schedule the removal (this will handle future shift removal automatically)
      const payload = {
        removeReason: removeReason,
        supportWorkerId: worker.supportWorkerId,
        removedOn: removalDateISO,
      };

      await doRemoveTeamMember(payload);

      setTitle('User successfully scheduled for removal');
      setStep(3);
    } catch (error) {
      console.error('Error scheduling team member removal:', error);
      notification.error({ message: 'Something went wrong, please try again.' });
    }

    setIsLoading(false);
  };

  const onChangeRemovalDate = (date: Date | null) => {
    setRemovalDate(date);
  };

  const onChangeRemoveReason = (reason: string) => {
    setRemoveReason(reason);
  };

  const isFormValid = removeReason && removeReason.trim().length >= 5 && (!isSelectFutureDate || removalDate);

  const getModalProps = () => {
    if (step === 1) {
      return {
        cancelProps: {
          label: 'Cancel',
          onClick: onModalClose,
          loading: isLoading,
        },
        confirmProps: {
          label: 'Next',
          onClick: onModalSave,
          loading: isLoading,
          disabled: !isFormValid,
        },
      };
    }
    if (step === 2) {
      return {
        cancelProps: {
          label: 'Cancel',
          onClick: onModalClose,
          loading: isLoading,
        },
        confirmProps: {
          label: 'Remove from Shifts & Schedule Removal',
          onClick: onConfirmRemoval,
          loading: isLoading,
          color: 'red',
        },
      };
    }
    return {};
  };

  const showInModal =
    isEmpty(worker) || ![1, 2, 3].includes(step) ? (
      <></>
    ) : step === 1 ? (
      (() => {
        return (
          <PermanentlyRemoveUserModalStep1
            worker={worker}
            isSelectFutureDate={isSelectFutureDate}
            removalDate={removalDate}
            removeReason={removeReason}
            onCheckBoxChange={onCheckBoxChange}
            onChangeRemovalDate={onChangeRemovalDate}
            onChangeRemoveReason={onChangeRemoveReason}
          />
        );
      })()
    ) : step === 2 ? (
      //step 2 - Impacted Shifts
      <PermanentlyRemoveUserModalStep2ImpactedShifts
        worker={worker}
        impactedShifts={impactedShifts}
        removalDate={removalDate ? moment(removalDate).format('YYYY-MM-DD') : ''}
      />
    ) : (
      //step 3 - Success
      <PermanentlyRemoveUserModalStep2
        worker={worker}
        isSelectFutureDate={isSelectFutureDate}
        removalDate={removalDate ? moment(removalDate).format('YYYY-MM-DD') : ''}
        removeReason={removeReason}
        onClose={onModalClose}
      />
    );

  return (
    <Modal
      title={!isEmpty(worker) ? `${title} ${worker.firstName} ${worker.lastName}` : ''}
      opened={isOpen}
      onClose={onModalClose}
      closeOnClickOutside={!isLoading}
      size='md'
      {...getModalProps()}
    >
      <Stack gap='sm'>{showInModal}</Stack>
    </Modal>
  );
};

export { PermanentlyRemoveUserModal };
